import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { HIT_BOARD_STATUS } from 'redux/actions/board';
import {
  GET_COMPETENCY_INQUIRY,
  GET_COMPETENCY_LIB,
  GET_COP_LIST,
  GET_CUSTOM_MANAGEMENT,
  GET_DETAIL_COMPANY,
  GET_DETAIL_COMPETENCY,
  GET_LIST_LEVEL_DESCRIPTION,
  GET_LIST_MEMBER,
  GET_LIST_SKILL_GROUP,
  GET_LIST_SUB_SKILL_GROUP,
  GET_LIST_SUB_SKILL_GROUP_MULTIPLE,
  GET_UPDATE_SUB_SKILL_GROUP_MULTIPLE,
  HIT_COMPETENCY_LIB,
  HIT_COP_LIST,
  HIT_CUSTOM_MANAGEMENT,
  HIT_LIST_LEVEL_DESCRIPTION,
  HIT_LIST_MEMBER,
  HIT_LIST_SKILL_GROUP,
} from 'redux/actions/company';
import { HIT_UPDATE_PROFILE } from 'redux/actions/profile';
import { HIT_ACTIVITY_TYPE_LIST } from 'redux/actions/wla';
import {
  DO_LOADING, DO_SWITCH_OFF, DO_SWITCH_ON, DO_TRIGGER,
} from 'redux/actions/loading';
import useTransformLoader from 'components/transformLoader';
import { apiCreateActivityType, apiUpdateActivityType } from 'redux/api/wla';
import {
  apiCompetencyDelete,
  apiCompetencyInquiry,
  apiCompetencyLib,
  apiCompetencyLibCreate,
  apiCompetencyLibUpdate,
  apiCompetencyMove,
  apiCopCreate,
  apiCopList,
  apiCopUpdate,
  apiCustomManagement,
  apiCustomManagementCreate,
  apiCustomManagementUpdate,
  apiDeleteCompanyMember,
  apiDetailCompany,
  apiDetailCompetency,
  apiImportCompetencyLib,
  apiInviteCompanyMember,
  apiListCompanyMember,
  apiListLevelDesc,
  apiMoveOrderActivity,
  apiMoveOrderFteLimit,
  apiSkillGroupAdd,
  apiSkillGroupList,
  apiSubSkillGroupList,
  apiUpdateCompany,
  apiUpdateLevelDesc,
} from 'redux/api/company';

interface ActionPayload {
  readonly type: string;
  readonly payload?: unknown;
  readonly [key: string]: unknown;
}

interface ApiMessage {
  readonly message?: string;
  readonly data?: unknown;
}

const random = () => Math.random()?.toString(36).slice(-5);
const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));
const getMessage = (response: unknown, fallback: string) => (response as ApiMessage)?.message ?? fallback;
const getMutationData = (payload: unknown) => (payload as { readonly data?: unknown })?.data ?? payload;

type SubSkillGroupItem = Readonly<{
  competency_sub_skill_group_id?: unknown;
  status?: string;
  sub_skill_group_name?: unknown;
}>;

const mapActiveSubSkillGroups = (response: unknown) => (response as { readonly data?: readonly SubSkillGroupItem[] })?.data
  ?.filter((item) => item.status === 'active')
  .map((item) => ({ value: item.competency_sub_skill_group_id, label: item.sub_skill_group_name }));

const useReduxBridge = () => {
  const dispatch: (action: ActionPayload) => void = useDispatch();
  const { tf } = useTransformLoader();

  const setLoading = (value: boolean) => dispatch({ type: DO_LOADING, payload: value });
  const trigger = () => dispatch({ type: DO_TRIGGER, payload: random() });
  const status = (statusValue: 'success' | 'failed', message: string) => dispatch({
    type: HIT_BOARD_STATUS,
    payload: { status: statusValue, message },
  });
  const switchOn = (key: string) => dispatch({ type: DO_SWITCH_ON, payload: key });
  const switchOff = (key: string) => dispatch({ type: DO_SWITCH_OFF, payload: key });

  return {
    dispatch,
    setLoading,
    status,
    switchOff,
    switchOn,
    tf,
    trigger,
  };
};

export function useListCompanyMemberQuery(enabled: boolean = true) {
  const bridge = useReduxBridge();
  const loaderKey = bridge.tf('company').memberCompany;

  return useQuery({
    queryKey: ['company-members'],
    queryFn: async () => {
      bridge.switchOn(loaderKey);
      const response = await apiListCompanyMember({});
      bridge.dispatch({ type: GET_LIST_MEMBER, payload: response });
      bridge.switchOff(loaderKey);
      bridge.trigger();
      return response;
    },
    enabled,
    meta: {
      onError: () => {
        bridge.switchOff(loaderKey);
        bridge.trigger();
      },
    },
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useInviteCompanyMemberMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: apiInviteCompanyMember,
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.dispatch({ type: HIT_LIST_MEMBER });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useDetailCompanyQuery(payload: unknown, enabled: boolean = true) {
  const bridge = useReduxBridge();
  const loaderKey = bridge.tf('company').detailCompany;

  return useQuery({
    queryKey: ['company-detail', payload],
    queryFn: async () => {
      bridge.switchOn(loaderKey);
      const response = await apiDetailCompany(payload);
      bridge.setLoading(false);
      bridge.trigger();
      bridge.dispatch({ type: GET_DETAIL_COMPANY, payload: response });
      return response;
    },
    enabled,
    meta: {
      onError: () => {
        bridge.setLoading(false);
        bridge.trigger();
      },
    },
  });
}

export function useUpdateCompanyMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: apiUpdateCompany,
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.dispatch({
        type: HIT_UPDATE_PROFILE,
        payload: { type: 'company', message: getMessage(response, 'Success') },
      });
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCopListQuery(payload?: unknown, enabled: boolean = true) {
  const bridge = useReduxBridge();
  const loaderKey = bridge.tf('company').companyCustomOrganization;

  return useQuery({
    queryKey: ['company-cop-list', payload],
    queryFn: async () => {
      bridge.switchOn(loaderKey);
      const response = await apiCopList(payload);
      bridge.dispatch({ type: GET_COP_LIST, payload: response });
      bridge.switchOff(loaderKey);
      bridge.trigger();
      return response;
    },
    enabled,
    meta: {
      onError: (error: unknown) => {
        bridge.switchOff(loaderKey);
        bridge.trigger();
        bridge.status('failed', getErrorMessage(error));
      },
    },
  });
}

export function useCopCreateMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: apiCopCreate,
    onMutate: () => bridge.setLoading(true),
    onSuccess: () => {
      bridge.dispatch({ type: HIT_COP_LIST });
      bridge.status('success', 'Data berhasil di simpan');
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCopUpdateMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: (payload: unknown) => apiCopUpdate(getMutationData(payload)),
    onMutate: () => bridge.setLoading(true),
    onSuccess: () => {
      bridge.dispatch({ type: HIT_COP_LIST });
      bridge.status('success', 'perubahan data berhasil disimpan');
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCustomManagementQuery(enabled: boolean = true) {
  const bridge = useReduxBridge();
  const loaderKey = bridge.tf('company').companyCategoryGroup;

  return useQuery({
    queryKey: ['company-custom-management'],
    queryFn: async () => {
      bridge.switchOn(loaderKey);
      const response = await apiCustomManagement({});
      bridge.dispatch({ type: GET_CUSTOM_MANAGEMENT, payload: { data: response } });
      bridge.switchOff(loaderKey);
      bridge.trigger();
      return response;
    },
    enabled,
    meta: {
      onError: (error: unknown) => {
        bridge.switchOff(loaderKey);
        bridge.trigger();
        bridge.status('failed', getErrorMessage(error));
      },
    },
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useCustomManagementCreateMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: (payload: unknown) => apiCustomManagementCreate(getMutationData(payload)),
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.dispatch({ type: HIT_CUSTOM_MANAGEMENT });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCustomManagementUpdateMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: (payload: unknown) => apiCustomManagementUpdate(getMutationData(payload)),
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.dispatch({ type: HIT_CUSTOM_MANAGEMENT });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCompetencyLibQuery(payload?: unknown, enabled: boolean = true) {
  const bridge = useReduxBridge();
  const loaderKey = bridge.tf('company').companyLibray;

  return useQuery({
    queryKey: ['company-competency-lib', payload],
    queryFn: async () => {
      bridge.switchOn(loaderKey);
      const response = await apiCompetencyLib(payload);
      bridge.dispatch({ type: GET_COMPETENCY_LIB, payload: { data: response } });
      bridge.switchOff(loaderKey);
      bridge.trigger();
      return response;
    },
    enabled,
    meta: {
      onError: () => {
        bridge.dispatch({ type: GET_COMPETENCY_LIB, payload: 'RESET' });
        bridge.switchOff(loaderKey);
        bridge.trigger();
      },
    },
    refetchOnWindowFocus: false,
    retry: false,
  });
}

const getCompetencyListPayload = (payload: unknown) => ({
  company_id: (payload as { readonly data?: { readonly company_id?: unknown } })?.data?.company_id,
  status: 'active',
  type: (payload as { readonly data?: { readonly type?: unknown } })?.data?.type,
});

export function useCompetencyLibCreateMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: async (payload: { readonly data: unknown }) => {
      const response = await apiCompetencyLibCreate(payload.data);
      const list = await apiCompetencyLib(getCompetencyListPayload(payload));
      return { list, response };
    },
    onMutate: () => bridge.setLoading(true),
    onSuccess: ({ list, response }) => {
      bridge.dispatch({ type: GET_COMPETENCY_LIB, payload: { data: list } });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCompetencyLibUpdateMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: async (payload: { readonly data: unknown }) => {
      const response = await apiCompetencyLibUpdate(payload.data);
      const list = await apiCompetencyLib(getCompetencyListPayload(payload));
      return { list, response };
    },
    onMutate: () => bridge.setLoading(true),
    onSuccess: ({ list, response }) => {
      bridge.dispatch({ type: GET_COMPETENCY_LIB, payload: { data: list } });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCompetencyImportMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: async (payload: { readonly company_id?: unknown; readonly competencies?: readonly { readonly type?: unknown }[] }) => {
      const response = await apiImportCompetencyLib(payload);
      const list = await apiCompetencyLib({
        company_id: payload.company_id,
        status: 'active',
        type: payload.competencies?.[0]?.type,
      });
      return { list, response };
    },
    onMutate: () => bridge.setLoading(true),
    onSuccess: ({ list, response }) => {
      bridge.dispatch({ type: GET_COMPETENCY_LIB, payload: { data: list } });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCompetencyInquiryMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: async (payload: { readonly competency_id: unknown; readonly setShowInquiryComp?: (value: boolean) => void }) => {
      bridge.setLoading(true);
      payload.setShowInquiryComp?.(true);
      return apiCompetencyInquiry({ competency_id: payload.competency_id });
    },
    onSuccess: (response) => {
      bridge.dispatch({ type: GET_COMPETENCY_INQUIRY, payload: { datas: response } });
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.dispatch({ type: GET_COMPETENCY_INQUIRY, payload: [] });
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCompetencyDeleteMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: async (payload: { readonly datas: { readonly company_id?: unknown; readonly type?: unknown; readonly status?: unknown }; readonly competency_id: unknown }) => {
      const response = await apiCompetencyDelete({ competency_id: payload.competency_id });
      return { payload, response };
    },
    onMutate: () => bridge.setLoading(true),
    onSuccess: ({ payload }) => {
      bridge.dispatch({
        type: HIT_COMPETENCY_LIB,
        payload: {
          company_id: payload.datas.company_id,
          status: payload.datas.status,
          type: payload.datas.type,
        },
      });
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('success', 'berhasil dihapus');
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useDetailCompetencyQuery(payload?: unknown, enabled: boolean = true) {
  const bridge = useReduxBridge();
  const loaderKey = bridge.tf('company').companyCompetencyDetail;

  return useQuery({
    queryKey: ['company-detail-competency', payload],
    queryFn: async () => {
      bridge.switchOn(loaderKey);
      const response = await apiDetailCompetency(payload);
      bridge.dispatch({ type: GET_DETAIL_COMPETENCY, payload: { data: response } });
      bridge.switchOff(loaderKey);
      bridge.trigger();
      return response;
    },
    enabled,
    meta: {
      onError: () => {
        bridge.switchOff(loaderKey);
        bridge.trigger();
        bridge.dispatch({ type: GET_DETAIL_COMPETENCY, payload: { data: [] } });
      },
    },
  });
}

export function useListLevelDescriptionQuery(payload?: unknown, enabled: boolean = true) {
  const bridge = useReduxBridge();
  const loaderKey = bridge.tf('company').companyLevelDescription;

  return useQuery({
    queryKey: ['company-level-description', payload],
    queryFn: async () => {
      bridge.switchOn(loaderKey);
      const response = await apiListLevelDesc(payload);
      bridge.dispatch({ type: GET_LIST_LEVEL_DESCRIPTION, payload: { data: response } });
      bridge.switchOff(loaderKey);
      bridge.trigger();
      return response;
    },
    enabled,
    meta: {
      onError: (error: unknown) => {
        bridge.switchOff(loaderKey);
        bridge.trigger();
        bridge.status('failed', getErrorMessage(error));
      },
    },
  });
}

export function useUpdateLevelDescriptionMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: (payload: unknown) => apiUpdateLevelDesc(getMutationData(payload)),
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.dispatch({
        type: HIT_LIST_LEVEL_DESCRIPTION,
        payload: { company_id: localStorage.getItem('company_id') },
      });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useActivityTypeCreateMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: (payload: unknown) => apiCreateActivityType(getMutationData(payload)),
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.dispatch({ type: HIT_ACTIVITY_TYPE_LIST });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useActivityTypeUpdateMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: (payload: unknown) => apiUpdateActivityType(getMutationData(payload)),
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.dispatch({ type: HIT_ACTIVITY_TYPE_LIST });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useActivityMoveOrderMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: (payload: unknown) => apiMoveOrderActivity(getMutationData(payload)),
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useFteLimitMoveOrderMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: (payload: unknown) => apiMoveOrderFteLimit(getMutationData(payload)),
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useSkillGroupListQuery(enabled: boolean = true) {
  const bridge = useReduxBridge();
  const loaderKey = bridge.tf('company').list_skill_group;

  return useQuery({
    queryKey: ['company-skill-group-list'],
    queryFn: async () => {
      bridge.switchOn(loaderKey);
      const response = await apiSkillGroupList({});
      bridge.dispatch({ type: GET_LIST_SKILL_GROUP, payload: response });
      bridge.switchOff(loaderKey);
      bridge.trigger();
      return response;
    },
    enabled,
    meta: {
      onError: (error: unknown) => {
        bridge.switchOff(loaderKey);
        bridge.trigger();
        bridge.status('failed', getErrorMessage(error));
      },
    },
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useSubSkillGroupListQuery(payload?: unknown, enabled: boolean = true) {
  const bridge = useReduxBridge();
  const loaderKey = bridge.tf('company').list_sub_skill_group;

  return useQuery({
    queryKey: ['company-sub-skill-group-list', payload],
    queryFn: async () => {
      bridge.switchOn(loaderKey);
      const response = await apiSubSkillGroupList(payload);
      bridge.dispatch({ type: GET_LIST_SUB_SKILL_GROUP, payload: response });
      bridge.switchOff(loaderKey);
      bridge.trigger();
      return response;
    },
    enabled,
    meta: {
      onError: (error: unknown) => {
        bridge.switchOff(loaderKey);
        bridge.trigger();
        bridge.status('failed', getErrorMessage(error));
      },
    },
  });
}

export function useSubSkillGroupMultipleMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: async (payload: { readonly payload: unknown; readonly index?: number; readonly isMultipleSubSkillGroup?: boolean }) => {
      if (!payload.isMultipleSubSkillGroup && payload.index === undefined) {
        const response = await apiSubSkillGroupList(payload.payload);
        return { kind: 'single' as const, response };
      }
      if (!payload.isMultipleSubSkillGroup && payload.index !== undefined) {
        const response = await apiSubSkillGroupList(payload.payload);
        const list = mapActiveSubSkillGroups(response);
        return { index: payload.index, kind: 'indexed' as const, list };
      }
      const result = await Promise.all((payload.payload as readonly unknown[]).map((item) => apiSubSkillGroupList(item)));
      const list = result.map(mapActiveSubSkillGroups);
      return { kind: 'multiple' as const, list };
    },
    onMutate: () => {
      bridge.switchOn(bridge.tf('company').list_sub_skill_group);
      bridge.switchOn(bridge.tf('company').list_multiple_sub_skill_group);
    },
    onSuccess: (result) => {
      if (result.kind === 'single') {
        bridge.dispatch({ type: GET_LIST_SUB_SKILL_GROUP, payload: result.response });
      }
      if (result.kind === 'indexed') {
        bridge.dispatch({ type: GET_UPDATE_SUB_SKILL_GROUP_MULTIPLE, payload: { index: result.index, list: result.list } });
      }
      if (result.kind === 'multiple') {
        bridge.dispatch({ type: GET_LIST_SUB_SKILL_GROUP_MULTIPLE, payload: result.list });
      }
      bridge.switchOff(bridge.tf('company').list_sub_skill_group);
      bridge.switchOff(bridge.tf('company').list_multiple_sub_skill_group);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.switchOff(bridge.tf('company').list_sub_skill_group);
      bridge.switchOff(bridge.tf('company').list_multiple_sub_skill_group);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useSkillGroupActionMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: apiSkillGroupAdd,
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.dispatch({ type: HIT_LIST_SKILL_GROUP });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.trigger();
      bridge.setLoading(false);
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.trigger();
      bridge.status('failed', getErrorMessage(error));
    },
  });
}

export function useCompetencyMoveMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: apiCompetencyMove,
    onSuccess: (_response, payload) => {
      bridge.dispatch({ type: HIT_COMPETENCY_LIB, payload });
    },
  });
}

export function useCompanyFeedback() {
  const bridge = useReduxBridge();

  return {
    setLoading: bridge.setLoading,
    setStatus: bridge.status,
    trigger: bridge.trigger,
  };
}

export function useDeleteCompanyMemberMutation() {
  const bridge = useReduxBridge();

  return useMutation({
    mutationFn: apiDeleteCompanyMember,
    onMutate: () => bridge.setLoading(true),
    onSuccess: (response) => {
      bridge.dispatch({ type: HIT_LIST_MEMBER });
      bridge.status('success', getMessage(response, 'Success'));
      bridge.setLoading(false);
      bridge.trigger();
    },
    onError: (error) => {
      bridge.setLoading(false);
      bridge.status('failed', getErrorMessage(error));
    },
  });
}
